<template>
  <transition name="fade-scale">
    <div
      v-if="isVisible"
      id="countdown-timer-modal"
      class="confirmation-modal c-modal-backdrop-wr"
    >
      <div class="c-modal">
        <div class="window">
          <icon-component
            iconName="close"
            class="close-btn"
            @click="close"
          />

          <div
            v-if="title"
            class="title"
          >
            {{ title }}
          </div>

          <div class="message">
            {{ message }}
          </div>

          <div
            class="timer"
          >
            {{ timerOutput }}
          </div>

          <base-button
            :text="buttonLabel"
            classes="ok-button def-btn main-action-btn uppercase medium border-2"
            @onClick="_confirm"
          />
        </div>
      </div>

      <div class="c-modal-backdrop"></div>
    </div>
  </transition>
</template>

<script>
import BaseButton from "@/components/_shared/Button.vue";
import iconComponent from "@/components/_shared/Icon.vue";

export default {
  name: 'CountdownTimerModal',

  emits: ['onCountdownTimerModalClose', 'onCountdownTimerModalConfirm'],

  components: {
    BaseButton,
    iconComponent
  },
  
  data() {
    return {
      isVisible: false,
      title: '',
      message: '',
      buttonLabel: '',

      expiredAt: null,

      timerInterval: null,

      timerOutput: '',
    }
  },

  watch: {
    isVisible(res) {
      if (res) {
        document.body.classList.add("modal-open");

        return
      }

      setTimeout(() => {
        document.body.classList.remove("modal-open");
      }, 300);
    },

    currentTime: {
      handler(time) {
        if (time === 0) {
          this.stopTimer();
        }
      },

      deep: true,
    },
  },

  beforeUnmount() {
    this.emmiter.emit('toggleModals', {opened: false, type: 'CountdownTimerModal'});
  },
  
  methods: {
    escListener(e) {
      if (e.code === 'Escape') {
        this.close();
      }
    },

    open() {
      this.isVisible = true;
      this.emmiter.emit('toggleModals', {opened: true, type: 'CountdownTimerModal'});
      document.addEventListener('keydown', this.escListener);

     this.startTimer();
    },

    close() {
      this.isVisible = false;
      document.removeEventListener('keydown', this.escListener);
      this.emmiter.emit('toggleModals', {opened: false, type: 'CountdownTimerModal'});

      setTimeout(() => {
        this.$emit('onCountdownTimerModalClose');
      }, 300);

      this.timerOutput = '';

      this.stopTimer();
    },

    show({ title, message, buttonLabel, expired_at }) {
      this.title = title;
      this.message = message;
      this.buttonLabel = buttonLabel;
      this.expiredAt = new Date(expired_at).getTime();

      if (!expired_at) {
        this.close();
        return;
      }

      this.open();
    },

    _confirm() {
      this.$emit('onCountdownTimerModalConfirm');
      this.close();
    },

    _cancel() {
      this.close();
    },

    startTimer() {
      this.getCountdownTime();

      this.timerInterval = setInterval(this.getCountdownTime, 1000);
    },

    stopTimer() {
      clearTimeout(this.timerInterval);
    },

    getCountdownTime() {
      const timeNow = new Date().getTime();

      const timeDifference = this.expiredAt - timeNow;

      const millisecondsInOneSecond = 1000;
      const millisecondsInOneMinute = millisecondsInOneSecond * 60;
      const millisecondsInOneHour = millisecondsInOneMinute * 60;

      const remainderDifferenceInMinutes = (timeDifference % millisecondsInOneHour) / millisecondsInOneMinute;
      const remainderDifferenceInSeconds = (timeDifference % millisecondsInOneMinute) / millisecondsInOneSecond;

      const remainingMinutes = Math.floor(remainderDifferenceInMinutes);
      const remainingSeconds = Math.floor(remainderDifferenceInSeconds);

      const needStopTime = Boolean(remainingMinutes <= 0 && remainingSeconds <= 0);
      if (needStopTime) {
        this.stopTimer();
        this.close();

        return;
      }

      this.timerOutput = `${remainingMinutes} minutes ${remainingSeconds} seconds left`;
    }
  },
}
</script>

<style lang="less" scoped>
.fade-scale-enter-active, .fade-scale-leave-active {
  transition: all 0.3s;
  .window {
    transition: all 0.3s;
  }
}
.fade-scale-enter-from,
.fade-scale-leave-to {
  opacity: 0;
  .window {
    -webkit-transform: scale(1.1);
    transform: scale(1.1);
  }
}

.c-modal-backdrop-wr {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;

  .c-modal {
    overflow-x: auto;
    display: flex;
    flex-direction: column;
    z-index: 1041;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;

    --color-background: #ffffff;
    --color-title: #3F8EC5;
    --color-text: #1C282D;
    --color-backdrop: #000000;

    .window {
      background: var(--color-background);
      border-radius: 8px;
      box-shadow: 4px 4px 12px 0 rgba(0, 0, 0, 0.12);
      width: 488px;
      height: auto;
      min-height: 150px;
      margin-left: auto;
      margin-right: auto;
      padding: 23px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-direction: column;
      flex-wrap: wrap;
      position: relative;
      box-sizing: border-box;

      * > {
        font-family: 'Segoe UI',sans-serif;
        font-style: normal;
      }

      .close-btn {
        position: absolute;
        top: 12px;
        right: 12px;
        cursor: pointer;
      }

      .ok-button {
        width: 100%;
      }

      .title {
        font-weight: 300;
        font-size: 42px;
        line-height: 56px;

        color: var(--color-title);
      }

      .message {
        font-weight: 600;
        font-size: 16px;
        line-height: 21px;
        letter-spacing: -0.3px;
        padding: 5px 0 15px 0;

        color: var(--color-text);
      }

      .timer {
        font-weight: 600;
        font-size: 28px;
        line-height: 37px;
        text-align: center;

        padding-bottom: 40px;

        letter-spacing: -0.4px;

        color: var(--color-title);
      }
    }
  }

  .c-modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1040;
    width: 100vw;
    height: 100vh;
    background-color: var(--color-backdrop);
    opacity: 0;
  }
}
</style>
